import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Mail, 
  ArrowLeft, 
  ArrowRight,
  Check,
  Clock,
  AlertTriangle,
  Users,
  Inbox,
  Send,
  Star,
  Sparkles,
  BarChart3,
  Zap,
  RefreshCw,
  Sun,
  Moon,
  ChevronRight,
  Reply,
  Forward,
  Archive,
  Trash2,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Target,
  Timer,
  MessageSquare,
  FileText,
  X,
  Copy,
  Undo2,
  UserPlus
} from "lucide-react";
import { useTheme } from "../App";

const translations = {
  fr: {
    back: "Retour à l'accueil",
    title: "Découvrez ALIA en action",
    subtitle: "Explorez une démonstration interactive de la plateforme.",
    tabs: {
      inbox: "Boîte de réception",
      analytics: "Analytiques"
    },
    inbox: {
      title: "Boîte de réception intelligente",
      unread: "Non lus",
      urgent: "Urgent",
      toReply: "À répondre",
      delegated: "Délégués"
    },
    emailDetail: {
      aiSummary: "Résumé IA",
      suggestedActions: "Actions suggérées",
      generateReply: "Générer une réponse",
      generating: "Génération en cours...",
      generatedReply: "Réponse générée",
      reply: "Répondre",
      delegate: "Déléguer",
      followUp: "Relancer J+3",
      archive: "Archiver"
    },
    analytics: {
      title: "Tableau de bord analytique",
      timeRecovered: "Temps récupéré",
      hoursPerDay: "heures/jour",
      emailsProcessed: "Emails traités",
      thisWeek: "cette semaine",
      responseRate: "Taux de réponse",
      avgResponseTime: "Temps moyen",
      minutes: "minutes"
    },
    cta: {
      title: "Prêt à automatiser votre gestion d'emails ?",
      button: "Démarrer l'essai gratuit"
    },
    priority: {
      urgentImportant: "Action immédiate",
      notUrgentImportant: "À planifier",
      urgentNotImportant: "À déléguer",
      notUrgentNotImportant: "À archiver"
    },
    actions: {
      replyModal: {
        title: "Réponse IA",
        generating: "Génération de la réponse...",
        copy: "Copier",
        copied: "Copié !",
        close: "Fermer",
        send: "Envoyer"
      },
      delegateModal: {
        title: "Déléguer cet email",
        assignTo: "Assigner à",
        placeholder: "Nom du collaborateur",
        submit: "Déléguer",
        success: "Email délégué avec succès",
        badge: "Délégué"
      },
      followUp: {
        success: "Relance planifiée dans 3 jours",
        badge: "Relance J+3",
        logged: "Action enregistrée"
      },
      archive: {
        success: "Email archivé",
        undo: "Annuler",
        undone: "Archivage annulé"
      }
    },
    activityLog: {
      title: "Activité récente",
      delegated: "Délégué à",
      followUpScheduled: "Relance programmée",
      archived: "Archivé"
    }
  },
  en: {
    back: "Back to home",
    title: "Experience ALIA in action",
    subtitle: "Explore an interactive simulation of the platform.",
    tabs: {
      inbox: "Inbox",
      analytics: "Analytics"
    },
    inbox: {
      title: "Smart Inbox",
      unread: "Unread",
      urgent: "Urgent",
      toReply: "To Reply",
      delegated: "Delegated"
    },
    emailDetail: {
      aiSummary: "AI Summary",
      suggestedActions: "Suggested Actions",
      generateReply: "Generate Reply",
      generating: "Generating...",
      generatedReply: "Generated Reply",
      reply: "Reply",
      delegate: "Delegate",
      followUp: "Follow-up D+3",
      archive: "Archive"
    },
    analytics: {
      title: "Analytics Dashboard",
      timeRecovered: "Time Recovered",
      hoursPerDay: "hours/day",
      emailsProcessed: "Emails Processed",
      thisWeek: "this week",
      responseRate: "Response Rate",
      avgResponseTime: "Avg Response Time",
      minutes: "minutes"
    },
    cta: {
      title: "Ready to automate your email workflow?",
      button: "Start free trial"
    },
    priority: {
      urgentImportant: "Immediate action",
      notUrgentImportant: "Schedule",
      urgentNotImportant: "Delegate",
      notUrgentNotImportant: "Archive"
    },
    actions: {
      replyModal: {
        title: "AI Reply",
        generating: "Generating response...",
        copy: "Copy",
        copied: "Copied!",
        close: "Close",
        send: "Send"
      },
      delegateModal: {
        title: "Delegate this email",
        assignTo: "Assign to",
        placeholder: "Colleague name",
        submit: "Delegate",
        success: "Email delegated successfully",
        badge: "Delegated"
      },
      followUp: {
        success: "Follow-up scheduled in 3 days",
        badge: "Follow-up D+3",
        logged: "Action logged"
      },
      archive: {
        success: "Email archived",
        undo: "Undo",
        undone: "Archive undone"
      }
    },
    activityLog: {
      title: "Recent Activity",
      delegated: "Delegated to",
      followUpScheduled: "Follow-up scheduled",
      archived: "Archived"
    }
  }
};

// Mock email data
const mockEmails = {
  fr: [
    {
      id: 1,
      from: "Marc Dupont",
      email: "marc.dupont@clienta.fr",
      subject: "URGENT: Validation contrat Q1 2025",
      preview: "Bonjour, nous devons finaliser le contrat avant vendredi. Pourriez-vous...",
      time: "10:32",
      priority: "urgent",
      unread: true,
      aiSummary: "Marc demande une validation urgente du contrat Q1. Deadline: vendredi. Action requise: révision et signature.",
      suggestedReply: "Bonjour Marc,\n\nMerci pour votre message. J'ai bien noté l'urgence concernant le contrat Q1.\n\nJe reviens vers vous d'ici demain matin avec mes commentaires et la validation.\n\nCordialement,\n[Votre nom]"
    },
    {
      id: 2,
      from: "Sophie Martin",
      email: "sophie.m@partenaire.com",
      subject: "Proposition commerciale - Projet Alpha",
      preview: "Suite à notre appel de la semaine dernière, veuillez trouver ci-joint...",
      time: "09:15",
      priority: "important",
      unread: true,
      aiSummary: "Sophie envoie une proposition commerciale suite à un appel. Contient un devis et planning. À planifier pour revue cette semaine.",
      suggestedReply: "Bonjour Sophie,\n\nMerci pour cette proposition détaillée.\n\nJe vais l'examiner avec mon équipe et vous reviens avec nos commentaires d'ici la fin de semaine.\n\nBien cordialement,\n[Votre nom]"
    },
    {
      id: 3,
      from: "Support IT",
      email: "support@internal.com",
      subject: "Mise à jour système prévue ce soir",
      preview: "Information: une maintenance est prévue ce soir de 22h à 23h...",
      time: "08:45",
      priority: "low",
      unread: false,
      aiSummary: "Notification IT: maintenance système ce soir 22h-23h. Aucune action requise. Information seulement.",
      suggestedReply: null
    },
    {
      id: 4,
      from: "Claire Dubois",
      email: "claire.dubois@prospect.fr",
      subject: "RE: Demande d'information produit",
      preview: "Merci pour ces informations. J'aurais quelques questions supplémentaires...",
      time: "Hier",
      priority: "toReply",
      unread: true,
      aiSummary: "Claire pose des questions sur le produit. Prospect qualifié. Relance recommandée sous 24h pour maintenir l'engagement.",
      suggestedReply: "Bonjour Claire,\n\nMerci pour votre intérêt et vos questions.\n\nJe serais ravi d'organiser un appel rapide pour y répondre en détail. Seriez-vous disponible jeudi ou vendredi ?\n\nCordialement,\n[Votre nom]"
    },
    {
      id: 5,
      from: "Newsletter Tech",
      email: "news@techdigest.com",
      subject: "Les 10 tendances IA de 2025",
      preview: "Cette semaine dans notre newsletter: l'IA générative...",
      time: "Hier",
      priority: "archive",
      unread: false,
      aiSummary: "Newsletter marketing. Pas d'action requise. Peut être archivée.",
      suggestedReply: null
    }
  ],
  en: [
    {
      id: 1,
      from: "Marc Dupont",
      email: "marc.dupont@clienta.com",
      subject: "URGENT: Q1 2025 Contract Validation",
      preview: "Hello, we need to finalize the contract before Friday. Could you...",
      time: "10:32",
      priority: "urgent",
      unread: true,
      aiSummary: "Marc requests urgent Q1 contract validation. Deadline: Friday. Action required: review and signature.",
      suggestedReply: "Hi Marc,\n\nThank you for your message. I've noted the urgency regarding the Q1 contract.\n\nI'll get back to you by tomorrow morning with my comments and validation.\n\nBest regards,\n[Your name]"
    },
    {
      id: 2,
      from: "Sophie Martin",
      email: "sophie.m@partner.com",
      subject: "Commercial Proposal - Project Alpha",
      preview: "Following our call last week, please find attached...",
      time: "09:15",
      priority: "important",
      unread: true,
      aiSummary: "Sophie sends a commercial proposal following a call. Contains quote and timeline. Schedule for review this week.",
      suggestedReply: "Hi Sophie,\n\nThank you for this detailed proposal.\n\nI'll review it with my team and get back to you with our feedback by end of week.\n\nBest regards,\n[Your name]"
    },
    {
      id: 3,
      from: "IT Support",
      email: "support@internal.com",
      subject: "System update scheduled tonight",
      preview: "Notice: maintenance is scheduled tonight from 10pm to 11pm...",
      time: "08:45",
      priority: "low",
      unread: false,
      aiSummary: "IT notification: system maintenance tonight 10pm-11pm. No action required. Information only.",
      suggestedReply: null
    },
    {
      id: 4,
      from: "Claire Dubois",
      email: "claire.dubois@prospect.com",
      subject: "RE: Product information request",
      preview: "Thanks for this information. I have a few additional questions...",
      time: "Yesterday",
      priority: "toReply",
      unread: true,
      aiSummary: "Claire has product questions. Qualified prospect. Follow-up recommended within 24h to maintain engagement.",
      suggestedReply: "Hi Claire,\n\nThank you for your interest and questions.\n\nI'd be happy to schedule a quick call to answer them in detail. Would you be available Thursday or Friday?\n\nBest regards,\n[Your name]"
    },
    {
      id: 5,
      from: "Tech Newsletter",
      email: "news@techdigest.com",
      subject: "Top 10 AI trends for 2025",
      preview: "This week in our newsletter: generative AI...",
      time: "Yesterday",
      priority: "archive",
      unread: false,
      aiSummary: "Marketing newsletter. No action required. Can be archived.",
      suggestedReply: null
    }
  ]
};

const DemoLivePage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [lang, setLang] = useState("fr");
  const [activeTab, setActiveTab] = useState("inbox");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGeneratedReply, setShowGeneratedReply] = useState(false);
  const [animatedStats, setAnimatedStats] = useState({
    timeRecovered: 0,
    emailsProcessed: 0,
    responseRate: 0,
    avgResponseTime: 0
  });
  
  // New interactive states
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [generatedReplyText, setGeneratedReplyText] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [delegateName, setDelegateName] = useState("");
  const [emailStates, setEmailStates] = useState({});  // Track delegated, followUp, archived states per email
  const [activityLog, setActivityLog] = useState([]);
  const [undoTimer, setUndoTimer] = useState(null);
  const undoTimeoutRef = useRef(null);

  const t = translations[lang];
  const emails = mockEmails[lang];

  // Animate stats on analytics tab
  useEffect(() => {
    if (activeTab === "analytics") {
      const targets = { timeRecovered: 2.5, emailsProcessed: 127, responseRate: 94, avgResponseTime: 8 };
      const duration = 1500;
      const steps = 60;
      const interval = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setAnimatedStats({
          timeRecovered: Math.min(targets.timeRecovered * progress, targets.timeRecovered),
          emailsProcessed: Math.floor(targets.emailsProcessed * progress),
          responseRate: Math.floor(targets.responseRate * progress),
          avgResponseTime: Math.min(targets.avgResponseTime * progress, targets.avgResponseTime)
        });
        if (step >= steps) clearInterval(timer);
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [activeTab]);

  // Reset reply state when email changes
  useEffect(() => {
    setShowGeneratedReply(false);
    setIsGenerating(false);
    setShowReplyModal(false);
    setShowDelegateModal(false);
    setGeneratedReplyText("");
    setCopied(false);
  }, [selectedEmail]);

  // Cleanup undo timeout on unmount
  useEffect(() => {
    const timeoutRef = undoTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  const handleGenerateReply = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setShowGeneratedReply(true);
    }, 2000);
  };

  // ========== NEW ACTION HANDLERS ==========

  // 1. REPLY ACTION - Opens modal with AI-generated response
  const handleReplyAction = () => {
    setShowReplyModal(true);
    setIsGeneratingReply(true);
    setGeneratedReplyText("");
    setCopied(false);
    
    // Simulate AI generation with typing effect
    setTimeout(() => {
      setIsGeneratingReply(false);
      if (selectedEmail) {
        setGeneratedReplyText(selectedEmail.suggestedReply || "");
      }
    }, 2000);
  };

  const handleCopyReply = () => {
    if (generatedReplyText) {
      navigator.clipboard.writeText(generatedReplyText);
      setCopied(true);
      toast.success(t.actions.replyModal.copied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 2. DELEGATE ACTION - Mini form + badge
  const handleDelegateAction = () => {
    setShowDelegateModal(true);
    setDelegateName("");
  };

  const handleDelegateSubmit = (e) => {
    e.preventDefault();
    if (!delegateName.trim() || !selectedEmail) return;
    
    // Update email state
    setEmailStates(prev => ({
      ...prev,
      [selectedEmail.id]: {
        ...prev[selectedEmail.id],
        delegated: true,
        delegatedTo: delegateName.trim()
      }
    }));
    
    // Add to activity log
    setActivityLog(prev => [{
      id: Date.now(),
      type: 'delegated',
      emailId: selectedEmail.id,
      emailSubject: selectedEmail.subject,
      to: delegateName.trim(),
      timestamp: new Date()
    }, ...prev]);
    
    setShowDelegateModal(false);
    toast.success(t.actions.delegateModal.success, {
      description: `${t.activityLog.delegated} ${delegateName.trim()}`
    });
  };

  // 3. FOLLOW-UP J+3 ACTION
  const handleFollowUpAction = () => {
    if (!selectedEmail) return;
    
    // Update email state
    setEmailStates(prev => ({
      ...prev,
      [selectedEmail.id]: {
        ...prev[selectedEmail.id],
        followUp: true
      }
    }));
    
    // Add to activity log
    setActivityLog(prev => [{
      id: Date.now(),
      type: 'followUp',
      emailId: selectedEmail.id,
      emailSubject: selectedEmail.subject,
      timestamp: new Date()
    }, ...prev]);
    
    toast.success(t.actions.followUp.success, {
      description: t.actions.followUp.logged
    });
  };

  // 4. ARCHIVE ACTION with 5s undo
  const handleArchiveAction = () => {
    if (!selectedEmail) return;
    
    const emailId = selectedEmail.id;
    
    // Update email state
    setEmailStates(prev => ({
      ...prev,
      [emailId]: {
        ...prev[emailId],
        archived: true
      }
    }));
    
    // Add to activity log
    setActivityLog(prev => [{
      id: Date.now(),
      type: 'archived',
      emailId: emailId,
      emailSubject: selectedEmail.subject,
      timestamp: new Date()
    }, ...prev]);
    
    // Show toast with undo option
    const toastId = toast.success(t.actions.archive.success, {
      action: {
        label: t.actions.archive.undo,
        onClick: () => handleUndoArchive(emailId)
      },
      duration: 5000
    });
    
    // Auto-clear undo after 5 seconds (handled by toast duration)
  };

  const handleUndoArchive = (emailId) => {
    setEmailStates(prev => ({
      ...prev,
      [emailId]: {
        ...prev[emailId],
        archived: false
      }
    }));
    
    // Remove from activity log
    setActivityLog(prev => prev.filter(log => !(log.type === 'archived' && log.emailId === emailId)));
    
    toast.info(t.actions.archive.undone);
  };

  // Helper to get email state
  const getEmailState = (emailId) => {
    return emailStates[emailId] || {};
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "bg-red-500";
      case "important": return "bg-yellow-500";
      case "toReply": return "bg-orange-500";
      case "archive": return "bg-slate-500";
      default: return "bg-blue-500";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "urgent": return t.priority.urgentImportant;
      case "important": return t.priority.notUrgentImportant;
      case "toReply": return lang === "fr" ? "À répondre" : "To Reply";
      case "archive": return t.priority.notUrgentNotImportant;
      default: return priority;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLang("fr")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "fr" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "en" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  EN
                </button>
              </div>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-slate-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          {/* Title Section */}
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
              <Sparkles className="w-3 h-3 mr-1" />
              {lang === "fr" ? "Démonstration Interactive" : "Interactive Demo"}
            </Badge>
            <h1 
              className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3"
              style={{ fontFamily: 'Outfit' }}
              data-testid="demo-live-title"
            >
              {t.title}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t.subtitle}
            </p>
          </div>

          {/* Demo Container */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-12">
            {/* Mock Browser Header */}
            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="flex-1 ml-4">
                <div className="bg-white dark:bg-slate-700 rounded-md px-3 py-1 text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto text-center">
                  app.alia-assistant.com/dashboard
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-slate-200 dark:border-slate-700 px-4">
              <div className="flex gap-4">
                <button
                  onClick={() => { setActiveTab("inbox"); setSelectedEmail(null); }}
                  className={`py-3 px-4 border-b-2 transition-colors font-medium text-sm flex items-center gap-2 ${
                    activeTab === "inbox"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                  data-testid="tab-inbox"
                >
                  <Inbox className="w-4 h-4" />
                  {t.tabs.inbox}
                </button>
                <button
                  onClick={() => { setActiveTab("analytics"); setSelectedEmail(null); }}
                  className={`py-3 px-4 border-b-2 transition-colors font-medium text-sm flex items-center gap-2 ${
                    activeTab === "analytics"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                  data-testid="tab-analytics"
                >
                  <BarChart3 className="w-4 h-4" />
                  {t.tabs.analytics}
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="min-h-[600px]">
              {activeTab === "inbox" && (
                <div className="flex">
                  {/* Email List */}
                  <div className={`${selectedEmail ? 'w-2/5 border-r border-slate-200 dark:border-slate-700' : 'w-full'} transition-all duration-300`}>
                    {/* Stats Bar */}
                    <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 dark:bg-slate-800/50">
                      {[
                        { label: t.inbox.unread, value: emails.filter(e => e.unread && !getEmailState(e.id).archived).length, color: "bg-blue-500/10 text-blue-600" },
                        { label: t.inbox.urgent, value: emails.filter(e => e.priority === 'urgent' && !getEmailState(e.id).archived).length, color: "bg-red-500/10 text-red-600" },
                        { label: t.inbox.toReply, value: emails.filter(e => e.priority === 'toReply' && !getEmailState(e.id).archived).length, color: "bg-orange-500/10 text-orange-600" },
                        { label: t.inbox.delegated, value: Object.values(emailStates).filter(s => s.delegated).length, color: "bg-purple-500/10 text-purple-600" }
                      ].map((stat, i) => (
                        <div key={i} className={`rounded-lg p-2 text-center ${stat.color}`}>
                          <p className="text-xl font-bold">{stat.value}</p>
                          <p className="text-xs opacity-75">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Email List */}
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {emails.map((email) => {
                        const state = getEmailState(email.id);
                        const isArchived = state.archived;
                        const isDelegated = state.delegated;
                        const hasFollowUp = state.followUp;
                        
                        return (
                          <div
                            key={email.id}
                            onClick={() => !isArchived && setSelectedEmail(email)}
                            className={`p-4 transition-all ${
                              isArchived 
                                ? 'opacity-40 cursor-not-allowed bg-slate-100 dark:bg-slate-800/30' 
                                : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            } ${
                              selectedEmail?.id === email.id && !isArchived ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            } ${!isArchived && email.unread ? 'bg-white dark:bg-slate-900' : ''}`}
                            data-testid={`email-item-${email.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 ${!isArchived && email.unread ? getPriorityColor(email.priority) : 'bg-transparent'}`}></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`font-medium text-sm ${!isArchived && email.unread ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                    {email.from}
                                  </span>
                                  <span className="text-xs text-slate-400">{email.time}</span>
                                </div>
                                <p className={`text-sm mb-1 truncate ${!isArchived && email.unread ? 'text-slate-800 dark:text-slate-200 font-medium' : 'text-slate-600 dark:text-slate-400'}`}>
                                  {email.subject}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-500 truncate">
                                  {email.preview}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {!isArchived && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(email.priority)} text-white`}>
                                      {getPriorityLabel(email.priority)}
                                    </span>
                                  )}
                                  {isDelegated && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500 text-white">
                                      <UserPlus className="w-3 h-3 mr-1" />
                                      {t.actions.delegateModal.badge}
                                    </span>
                                  )}
                                  {hasFollowUp && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500 text-white">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {t.actions.followUp.badge}
                                    </span>
                                  )}
                                  {isArchived && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-400 text-white">
                                      <Archive className="w-3 h-3 mr-1" />
                                      {lang === 'fr' ? 'Archivé' : 'Archived'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {!isArchived && <ChevronRight className="w-4 h-4 text-slate-400 mt-2" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Email Detail */}
                  {selectedEmail && (
                    <div className="w-3/5 p-6 animate-fade-in" data-testid="email-detail-view">
                      {/* Email Header */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                            {selectedEmail.subject}
                          </h2>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(selectedEmail.priority)} text-white`}>
                            {getPriorityLabel(selectedEmail.priority)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                          <span className="font-medium text-slate-700 dark:text-slate-300">{selectedEmail.from}</span>
                          <span>&lt;{selectedEmail.email}&gt;</span>
                          <span className="ml-auto">{selectedEmail.time}</span>
                        </div>
                      </div>

                      {/* AI Summary Block */}
                      <div className="mb-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-blue-900 dark:text-blue-300">{t.emailDetail.aiSummary}</span>
                        </div>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          {selectedEmail.aiSummary}
                        </p>
                      </div>

                      {/* Suggested Actions */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-emerald-500" />
                          {t.emailDetail.suggestedActions}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={handleReplyAction}
                            data-testid="action-reply"
                          >
                            <Reply className="w-3.5 h-3.5 mr-1" />
                            {t.emailDetail.reply}
                          </Button>
                          <Button 
                            size="sm" 
                            variant={getEmailState(selectedEmail.id).delegated ? "default" : "outline"}
                            className={getEmailState(selectedEmail.id).delegated ? "bg-purple-500 hover:bg-purple-600 text-white" : ""}
                            onClick={handleDelegateAction}
                            disabled={getEmailState(selectedEmail.id).delegated}
                            data-testid="action-delegate"
                          >
                            <Users className="w-3.5 h-3.5 mr-1" />
                            {getEmailState(selectedEmail.id).delegated 
                              ? `${t.actions.delegateModal.badge}: ${getEmailState(selectedEmail.id).delegatedTo}`
                              : t.emailDetail.delegate
                            }
                          </Button>
                          <Button 
                            size="sm" 
                            variant={getEmailState(selectedEmail.id).followUp ? "default" : "outline"}
                            className={getEmailState(selectedEmail.id).followUp ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                            onClick={handleFollowUpAction}
                            disabled={getEmailState(selectedEmail.id).followUp}
                            data-testid="action-followup"
                          >
                            <Clock className="w-3.5 h-3.5 mr-1" />
                            {getEmailState(selectedEmail.id).followUp 
                              ? t.actions.followUp.badge
                              : t.emailDetail.followUp
                            }
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleArchiveAction}
                            disabled={getEmailState(selectedEmail.id).archived}
                            data-testid="action-archive"
                          >
                            <Archive className="w-3.5 h-3.5 mr-1" />
                            {t.emailDetail.archive}
                          </Button>
                        </div>
                        
                        {/* Status badges for selected email */}
                        {(getEmailState(selectedEmail.id).delegated || getEmailState(selectedEmail.id).followUp) && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {getEmailState(selectedEmail.id).delegated && (
                              <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                <UserPlus className="w-3 h-3 mr-1" />
                                {t.activityLog.delegated} {getEmailState(selectedEmail.id).delegatedTo}
                              </Badge>
                            )}
                            {getEmailState(selectedEmail.id).followUp && (
                              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                <Clock className="w-3 h-3 mr-1" />
                                {t.activityLog.followUpScheduled}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Email Content Preview */}
                      <div className="mb-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {selectedEmail.preview}...
                        </p>
                      </div>

                      {/* Generate Reply Section */}
                      {selectedEmail.suggestedReply && (
                        <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                          {!showGeneratedReply ? (
                            <Button 
                              onClick={handleGenerateReply}
                              disabled={isGenerating}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5"
                              data-testid="generate-reply-btn"
                            >
                              {isGenerating ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  {t.emailDetail.generating}
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  {t.emailDetail.generateReply}
                                </>
                              )}
                            </Button>
                          ) : (
                            <div className="animate-fade-in" data-testid="generated-reply">
                              <div className="flex items-center gap-2 mb-3">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                <span className="font-medium text-emerald-700 dark:text-emerald-400">
                                  {t.emailDetail.generatedReply}
                                </span>
                              </div>
                              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                <pre className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-sans">
                                  {selectedEmail.suggestedReply}
                                </pre>
                              </div>
                              <div className="flex gap-2 mt-4">
                                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                                  <Send className="w-4 h-4 mr-2" />
                                  {lang === "fr" ? "Envoyer" : "Send"}
                                </Button>
                                <Button variant="outline" className="flex-1">
                                  <FileText className="w-4 h-4 mr-2" />
                                  {lang === "fr" ? "Modifier" : "Edit"}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "analytics" && (
                <div className="p-6" data-testid="analytics-panel">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Outfit' }}>
                    {t.analytics.title}
                  </h3>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card className="p-4 text-center bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                      <Timer className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-blue-600">{animatedStats.timeRecovered.toFixed(1)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.hoursPerDay}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">{t.analytics.timeRecovered}</p>
                    </Card>
                    <Card className="p-4 text-center bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
                      <Mail className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-emerald-600">{animatedStats.emailsProcessed}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.thisWeek}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">{t.analytics.emailsProcessed}</p>
                    </Card>
                    <Card className="p-4 text-center bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                      <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-purple-600">{animatedStats.responseRate}%</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">&nbsp;</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">{t.analytics.responseRate}</p>
                    </Card>
                    <Card className="p-4 text-center bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
                      <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-orange-600">{animatedStats.avgResponseTime.toFixed(0)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.minutes}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1">{t.analytics.avgResponseTime}</p>
                    </Card>
                  </div>

                  {/* Priority Matrix Preview */}
                  <div className="mb-8">
                    <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {lang === "fr" ? "Matrice de Priorité" : "Priority Matrix"}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center hover:bg-red-500/20 transition-colors">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-red-500 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">{t.priority.urgentImportant}</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">1</p>
                      </div>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center hover:bg-yellow-500/20 transition-colors">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-yellow-500 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">{t.priority.notUrgentImportant}</p>
                        <p className="text-2xl font-bold text-yellow-600 mt-1">2</p>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center hover:bg-purple-500/20 transition-colors">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-500 flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-purple-600 dark:text-purple-400">{t.priority.urgentNotImportant}</p>
                        <p className="text-2xl font-bold text-purple-600 mt-1">0</p>
                      </div>
                      <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4 text-center hover:bg-slate-500/20 transition-colors">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-500 flex items-center justify-center">
                          <Archive className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t.priority.notUrgentNotImportant}</p>
                        <p className="text-2xl font-bold text-slate-600 mt-1">2</p>
                      </div>
                    </div>
                  </div>

                  {/* Weekly Activity Chart (Simple Bar) */}
                  <div>
                    <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {lang === "fr" ? "Activité hebdomadaire" : "Weekly Activity"}
                    </h4>
                    <div className="flex items-end gap-2 h-32 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                      {[65, 42, 78, 56, 89, 45, 23].map((value, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-blue-500 rounded-t transition-all duration-500"
                            style={{ height: `${value}%` }}
                          ></div>
                          <span className="text-xs text-slate-500">
                            {["L", "M", "M", "J", "V", "S", "D"][i]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center py-12 px-4 rounded-2xl bg-gradient-to-br from-blue-600/10 to-indigo-600/10 border border-blue-500/20">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4"
              style={{ fontFamily: 'Outfit' }}
            >
              {t.cta.title}
            </h2>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6"
              onClick={() => navigate("/register")}
              data-testid="demo-live-cta"
            >
              {t.cta.button}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
              <Link to="/legal" className="hover:text-blue-600">{lang === "fr" ? "Mentions légales" : "Legal"}</Link>
              <Link to="/privacy" className="hover:text-blue-600">{lang === "fr" ? "Confidentialité" : "Privacy"}</Link>
              <Link to="/contact" className="hover:text-blue-600">Contact</Link>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              © 2025 ALIA
            </p>
          </div>
        </div>
      </footer>

      {/* ========== MODALS ========== */}
      
      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="reply-modal">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                  {t.actions.replyModal.title}
                </h3>
              </div>
              <button 
                onClick={() => setShowReplyModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6">
              {isGeneratingReply ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">{t.actions.replyModal.generating}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    <pre className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-sans">
                      {generatedReplyText}
                    </pre>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleCopyReply}
                      variant="outline"
                      className={`flex-1 ${copied ? 'bg-green-50 border-green-500 text-green-600' : ''}`}
                      data-testid="copy-reply-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          {t.actions.replyModal.copied}
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          {t.actions.replyModal.copy}
                        </>
                      )}
                    </Button>
                    <Button 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        toast.success(lang === 'fr' ? 'Email envoyé !' : 'Email sent!');
                        setShowReplyModal(false);
                      }}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t.actions.replyModal.send}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delegate Modal */}
      {showDelegateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="delegate-modal">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                  {t.actions.delegateModal.title}
                </h3>
              </div>
              <button 
                onClick={() => setShowDelegateModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleDelegateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t.actions.delegateModal.assignTo}
                </label>
                <Input
                  value={delegateName}
                  onChange={(e) => setDelegateName(e.target.value)}
                  placeholder={t.actions.delegateModal.placeholder}
                  className="w-full"
                  autoFocus
                  data-testid="delegate-name-input"
                />
              </div>
              
              {/* Quick suggestions */}
              <div className="flex flex-wrap gap-2">
                {['Sophie M.', 'Marc D.', 'Claire B.'].map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setDelegateName(name)}
                    className="px-3 py-1.5 rounded-full text-sm bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
              
              <Button 
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={!delegateName.trim()}
                data-testid="delegate-submit-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t.actions.delegateModal.submit}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoLivePage;
